/**
 * MarkdownBlock — renders a `markdown` content block as formatted markdown.
 *
 * Rendered with `react-markdown` + `remark-gfm` (GFM tables / strikethrough /
 * task-lists). Configured SAFELY:
 *   - Raw HTML is NOT enabled (no `rehype-raw`) — react-markdown escapes any
 *     embedded HTML to text, so untrusted AI content cannot inject markup/XSS.
 *   - There is no `dangerouslySetInnerHTML` anywhere in this path.
 *   - Links open in a new tab with `rel="noopener noreferrer"`.
 *
 * Styling uses the `--mg-*` design tokens so it matches the chat design system.
 * The wrapper is a `<div>` (not AntD `<Text>`/`<span>`) because markdown emits
 * block-level elements (`<p>`, `<ul>`, headings, `<pre>`, `<table>`) which are
 * invalid inside a `<span>`. Colour is inherited from the surrounding message
 * bubble. Text stays user-selectable (no `userSelect` override).
 */

import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { MarkdownBlockProps } from '../../../types/xoe/XoeUiTypes';

const blockSpacing = '0 0 var(--mg-sp-4) 0';

const codeBase: React.CSSProperties = {
	fontFamily: 'var(--mg-font-mono)',
	fontSize: '0.9em',
};

// Module scope, never per render: a fresh `components` object re-renders the whole tree on every SSE frame.
const COMPONENTS: Components = {
	a: ({ node: _node, children, ...props }) => (
		<a
			{...props}
			target="_blank"
			rel="noopener noreferrer"
			style={{ color: 'var(--mg-primary)', textDecoration: 'underline' }}
		>
			{children}
		</a>
	),
	p: ({ node: _node, children, ...props }) => (
		<p {...props} style={{ margin: blockSpacing }}>
			{children}
		</p>
	),
	h1: ({ node: _node, children, ...props }) => (
		<h1 {...props} style={{ margin: blockSpacing, fontSize: 'var(--mg-fs-lg)', fontWeight: 600 }}>
			{children}
		</h1>
	),
	h2: ({ node: _node, children, ...props }) => (
		<h2 {...props} style={{ margin: blockSpacing, fontSize: 'var(--mg-fs-lg)', fontWeight: 600 }}>
			{children}
		</h2>
	),
	h3: ({ node: _node, children, ...props }) => (
		<h3 {...props} style={{ margin: blockSpacing, fontSize: 'var(--mg-fs-md)', fontWeight: 600 }}>
			{children}
		</h3>
	),
	h4: ({ node: _node, children, ...props }) => (
		<h4 {...props} style={{ margin: blockSpacing, fontSize: 'var(--mg-fs-md)', fontWeight: 600 }}>
			{children}
		</h4>
	),
	h5: ({ node: _node, children, ...props }) => (
		<h5 {...props} style={{ margin: blockSpacing, fontSize: 'var(--mg-fs-md)', fontWeight: 600 }}>
			{children}
		</h5>
	),
	h6: ({ node: _node, children, ...props }) => (
		<h6 {...props} style={{ margin: blockSpacing, fontSize: 'var(--mg-fs-md)', fontWeight: 600 }}>
			{children}
		</h6>
	),
	ul: ({ node: _node, children, ...props }) => (
		<ul {...props} style={{ margin: blockSpacing, paddingLeft: 'var(--mg-sp-7)' }}>
			{children}
		</ul>
	),
	ol: ({ node: _node, children, ...props }) => (
		<ol {...props} style={{ margin: blockSpacing, paddingLeft: 'var(--mg-sp-7)' }}>
			{children}
		</ol>
	),
	li: ({ node: _node, children, ...props }) => (
		<li {...props} style={{ marginBottom: 'var(--mg-sp-1)' }}>
			{children}
		</li>
	),
	blockquote: ({ node: _node, children, ...props }) => (
		<blockquote
			{...props}
			style={{
				margin: blockSpacing,
				paddingLeft: 'var(--mg-sp-4)',
				borderLeft: '3px solid var(--mg-border-strong)',
				color: 'var(--mg-text-secondary)',
			}}
		>
			{children}
		</blockquote>
	),
	code: ({ node: _node, className, children, ...props }) => {
		// react-markdown v10 drops the `inline` prop; fenced code blocks carry a
		// `language-*` className, inline code does not. The `<pre>` override below
		// supplies the block surface, so inline code gets its own subtle chip.
		const isInline = !className;
		if (isInline) {
			return (
				<code
					{...props}
					style={{
						...codeBase,
						background: 'var(--mg-bg-elevated)',
						border: '1px solid var(--mg-border)',
						borderRadius: 'var(--mg-radius-xs)',
						padding: '1px var(--mg-sp-2)',
					}}
				>
					{children}
				</code>
			);
		}
		return (
			<code {...props} className={className} style={codeBase}>
				{children}
			</code>
		);
	},
	pre: ({ node: _node, children, ...props }) => (
		<pre
			{...props}
			style={{
				...codeBase,
				margin: blockSpacing,
				padding: 'var(--mg-sp-4)',
				background: 'var(--mg-bg-elevated)',
				border: '1px solid var(--mg-border)',
				borderRadius: 'var(--mg-radius-sm)',
				overflowX: 'auto',
			}}
		>
			{children}
		</pre>
	),
	table: ({ node: _node, children, ...props }) => (
		<table
			{...props}
			style={{
				margin: blockSpacing,
				borderCollapse: 'collapse',
				width: '100%',
			}}
		>
			{children}
		</table>
	),
	th: ({ node: _node, children, ...props }) => (
		<th
			{...props}
			style={{
				border: '1px solid var(--mg-border)',
				padding: 'var(--mg-sp-2) var(--mg-sp-3)',
				textAlign: 'left',
				background: 'var(--mg-bg-subtle)',
				fontWeight: 600,
			}}
		>
			{children}
		</th>
	),
	td: ({ node: _node, children, ...props }) => (
		<td
			{...props}
			style={{
				border: '1px solid var(--mg-border)',
				padding: 'var(--mg-sp-2) var(--mg-sp-3)',
			}}
		>
			{children}
		</td>
	),
};

const MarkdownBlock: React.FC<MarkdownBlockProps> = ({ text }) => (
	<div
		className="mg-markdown"
		style={{
			color: 'inherit',
			wordBreak: 'break-word',
			fontSize: 'var(--mg-fs-md)',
			lineHeight: 1.5,
		}}
	>
		{/* Strip the trailing margin of the final block so spacing stays tight. */}
		<style>{`.mg-markdown > :last-child { margin-bottom: 0 !important; }`}</style>
		<ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
			{text}
		</ReactMarkdown>
	</div>
);

export default MarkdownBlock;
